import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { appendSearchParamsToURL } from "@/utils/utils";
import { Button, Card, Col, Row } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useEnumeration, useFilterCommissions } from "../../../../common/api/queries";
import AntIcon from "../../../../common/components/icons/AntIcon";
import BackNavigationArrow from "../../../../common/components/views/BackNavigationArrow";
import { Permission } from "../../../../common/security/enums";
import { TwoLevelEntityIdObject } from "../../../../common/types";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { EnumerationType } from "../../../enumerations/enums";
import { DEFAULT_FILTER_COMMISSIONS_QUERY_PARAMS } from "../api";
import { CommissionForm } from "../components/forms/CommissionForm";
import BatchCommissionFilterForm from "../components/forms/filter/BatchCommissionFilterForm";
import { BatchCommissionsTable } from "../components/tables/BatchCommissionsTable";
import { CommissionOriginType, CommissionsBatchStep } from "../enums";
import { CommissionsFilterPageRequest } from "../types";

export const CommissionsBatchManualImportsContainer = () => {
  const { t } = useTranslation();

  const { id1, id2 } = useParams<TwoLevelEntityIdObject>() as { id1: string; id2: string };
  const navigate = useNavigate();

  const institutionEnums = useEnumeration(EnumerationType.INSTITUTIONS);

  const [commissionFormOpen, setCommissionFormOpen] = useState(false);

  const searchParams = useFilterSearchParams<CommissionsFilterPageRequest>({
    params: ["processingResult", "postponementReasons"],
    defaultQueryParamsObject: DEFAULT_FILTER_COMMISSIONS_QUERY_PARAMS,
  });

  const { data } = useFilterCommissions({
    id: id1,
    object: { ...searchParams, institutionIds: [id2], origin: CommissionOriginType.MANUALLY_ADDED },
  });

  const handleFilterSubmit = (filter: CommissionsFilterPageRequest) => {
    navigate(appendSearchParamsToURL({ ...filter, pageIndex: undefined }), { replace: true });
  };

  return (
    <DisplayWrapper itemLoaded={!!data?.batch}>
      {data?.batch ? (
        <>
          <Card
            className="card-box"
            title={
              <BackNavigationArrow>
                <h2>
                  {t("commissions.batch.titles.manageManualImports", {
                    institutionName: institutionEnums.find((i) => i.id === id2)?.name || "",
                  })}
                </h2>
                <span className="sub-header-info normal-font-size">
                  {t("commissions.batch.titles.batchDetail", { name: data.batch.name })}
                </span>
              </BackNavigationArrow>
            }
            extra={
              <ComponentWithPermission permissions={[Permission.COMMISSIONS_MANAGE]}>
                {data.batch?.step !== CommissionsBatchStep.FINISH && (
                  <Button icon={<AntIcon type="plus" />} type="primary" onClick={() => setCommissionFormOpen(true)}>
                    {t("commissions.batch.actions.createCommission")}
                  </Button>
                )}
              </ComponentWithPermission>
            }
          >
            <Row justify="center">
              <Col flex="1000px">
                <BatchCommissionFilterForm filter={searchParams} onFilterSubmit={handleFilterSubmit} />
              </Col>
            </Row>
          </Card>
          <Card className="card-box">
            <BatchCommissionsTable
              batch={data.batch}
              institutionId={id2}
              filterData={{ ...searchParams, institutionIds: [id2], origin: CommissionOriginType.MANUALLY_ADDED }}
            />
          </Card>
        </>
      ) : undefined}

      <CommissionForm
        open={commissionFormOpen}
        formType="batchCommission"
        batchId={id1}
        institutionId={id2}
        onFormCancel={() => setCommissionFormOpen(false)}
      />
    </DisplayWrapper>
  );
};
